import React from 'react';
import _ from 'lodash';
import {connect} from 'react-redux';
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
  YAxis,
  XAxis
} from 'recharts';

import {
  fetchSchedulerRunsChartData,
  selectSchedulerRunsChartData
} from 'ducks/webhooks';

import ButtonDropdown from 'components/ButtonDropdown';

import {formatPeriod} from './utils';
import './styles.css';

const INTENSITY_OPTIONS = [
  {
    offset: {label: 'Past 5 minutes', value: 5},
    resolution: {label: '1 minute resolution', value: 1}
  },
  {
    offset: {label: 'Past hour', value: 60},
    resolution: {label: '5 minute resolution', value: 5}
  },
  {
    offset: {label: 'Past 2 hours', value: 120}, // 2 days == 120 minutes
    resolution: {label: '10 minutes resolution', value: 10}
  },
  {
    offset: {label: 'Past day', value: 1440}, // 1 day == 1440 minutes
    resolution: {label: '1 hour resolution', value: 60}
  },
  {
    offset: {label: 'Past 5 days', value: 7200}, // 5 days == 7200 minutes
    resolution: {label: '6 hours resolution', value: 360}
  },
  {
    offset: {label: 'Past week', value: 10080}, // 1 week == 7 days == 10080 minutes
    resolution: {label: '8 hours resolution', value: 480}
  },
  {
    offset: {label: 'Past month', value: 43200}, // 1 month ~= 30 days = 43200 minutes
    resolution: {label: '1 day resolution', value: 1440}
  }
];

const DEFAULT_INTENSITY_OPTION = INTENSITY_OPTIONS[0];

class SchedulerRunsChart extends React.Component<any> {
  state = {
    intensity: DEFAULT_INTENSITY_OPTION
  };

  componentDidMount() {
    this.fetchChartData();
  }

  fetchChartData = () =>
    this.props.fetchSchedulerRunsChartData({
      offset: this.state.intensity.offset.value,
      resolution: this.state.intensity.resolution.value
    });

  // Source: http://recharts.org/en-US/examples/CustomizedLabelLineChart
  customizedLabel = ({x, y, stroke, value}) => (
    <text x={x} y={y} dy={-4} fill={stroke} fontSize={15} textAnchor="middle">
      {value}
    </text>
  );

  customizedAxisTick = ({x, y, payload}) => (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#666"
        transform="rotate(-35)">
        {payload.value}
      </text>
    </g>
  );

  handleIntensityChange = intensity =>
    this.setState({intensity}, this.fetchChartData);

  render() {
    const {data} = this.props;
    let formattedData = [];

    if (!_.isEmpty(data)) {
      formattedData = data.map(row => ({
        name: formatPeriod(row),
        ...row
      }));
    }

    return (
      <div className="SchedulerRunsChart">
        <div className="heading">
          <h3 className="title">Scheduler Runs Line Chart</h3>
          <ButtonDropdown
            dropdownClassName="intensityDropdown"
            title={this.state.intensity.offset.label}>
            {INTENSITY_OPTIONS.map(opt => (
              <ButtonDropdown.Option
                className="intensityOption"
                key={opt.offset.label}
                onClick={() => this.handleIntensityChange(opt)}>
                <React.Fragment>
                  <h3>{opt.offset.label}</h3>
                  <p>{opt.resolution.label}</p>
                </React.Fragment>
              </ButtonDropdown.Option>
            ))}
          </ButtonDropdown>
        </div>
        <div className="chartContainer">
          <ResponsiveContainer>
            <AreaChart
              data={formattedData}
              margin={{top: 20, right: 30, left: -20, bottom: 5}}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                height={110}
                interval={0}
                tick={this.customizedAxisTick}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="pending"
                stroke="#8884d8"
                fill="#8884d8"
                label={this.customizedLabel}
                activeDot={{r: 5}}
              />
              <Area
                type="monotone"
                dataKey="active"
                stroke="#82ca9d"
                fill="#82ca9d"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  data: selectSchedulerRunsChartData(state)
});

const mapDispatchToProps = {fetchSchedulerRunsChartData};

export default connect(mapStateToProps, mapDispatchToProps)(SchedulerRunsChart);
